import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useHub } from "../lib/dados";
import {
  anotarAula,
  concluirAula,
  lerAulasDoBonus,
  mensagemDaEscrita,
  type AulaBonus,
  type CursoBonus as Curso,
} from "../lib/cursoBonus";
import { duracaoEmTexto, numeroDa, porSecao, progressoDe } from "../lib/aulasBonus";
import {
  TEMPOS_DO_DIA,
  TEMPO_PADRAO,
  aulasParaHoje,
  rotuloDoTempo,
  segundosEstudados,
  tempoDaBusca,
} from "../lib/planoEstudos";
import { mensagemDaAbertura, type MotivoAbertura } from "../lib/abrir";
import { Cabine } from "../componentes/FerramentaUi";
import { TileDeFato } from "../componentes/CalendarioUi";
import { Anotacao, LinhaDaAula, Tique } from "../componentes/CursoBonusUi";
import { Aviso, IconeExterno } from "../componentes/ui";
import Carregando from "../componentes/Carregando";

// O Plano de Estudos Inteligente (000382): o cronograma do Exame CFC que era um app da Lovable.
//
// As aulas são as do curso "CFC de A a Z – Turma Raiz" do Memberkit, numa ordem de estudo, e moram nas
// tabelas dos cursos bônus — por isso esta tela lê e grava pelas MESMAS funções da `CursoBonus`
// (`lerAulasDoBonus`, `concluirAula`, `anotarAula`) e abre cada aula pela mesma ponte. O que ela tem de
// próprio é a regra do dia (`lib/planoEstudos.ts`): o aluno escolhe quanto tempo tem, e a tela mostra
// as próximas aulas pendentes que cabem nele. O tempo mora no ENDEREÇO (`?tempo=90`), como toda escolha
// de tela desta casa: recarregar ou mandar o link não perde a escolha.
//
// O que ela NÃO faz: não decide acesso (o banco decide), não reordena as aulas (a ordem é a do plano,
// do banco) e não sabe se o vídeo foi assistido — "concluída" é a marca do aluno, como nos cursos bônus.
// O que o app de lá tinha e não veio: o cronômetro e os níveis. O progresso aqui é contagem, e o tempo
// estudado é a soma das aulas concluídas.

export default function PlanoEstudos({ card }: { card: string }) {
  const [params, setParams] = useSearchParams();
  const hub = useHub();
  const [estado, setEstado] = useState<
    { fase: "carregando" } | { fase: "pronto"; curso: Curso } | { fase: "recusado"; motivo: string } | { fase: "erro" }
  >({ fase: "carregando" });
  const [ocupadas, setOcupadas] = useState<Set<number>>(new Set());
  const [erros, setErros] = useState<Map<number, string>>(new Map());
  const [anotacaoAberta, setAnotacaoAberta] = useState<number | null>(null);

  // O curso: a mesma precedência da `CursoBonus` — a query que `aberturaDoCard` põe no link, senão o
  // único curso do card. O card é da Escola (os dois SKUs são um curso só), então a segunda vale quase
  // sempre.
  const daQuery = params.get("curso");
  const doCard =
    hub.fase === "pronto" ? hub.hub.cardsDeCurso.find((c) => c.card_slug === card)?.por_cursos ?? [] : [];
  const curso = daQuery ?? (doCard.length === 1 ? doCard[0] : null);
  const tempo = tempoDaBusca(params.get("tempo"));

  useEffect(() => {
    if (hub.fase !== "pronto") return;
    if (!curso) {
      setEstado({ fase: "recusado", motivo: "sem-acesso" });
      return;
    }
    let vivo = true;
    (async () => {
      const r = await lerAulasDoBonus(card, curso);
      if (!vivo) return;
      setEstado(r.fase === "pronto" ? { fase: "pronto", curso: r.curso } : r);
    })();
    return () => {
      vivo = false;
    };
  }, [card, curso, hub.fase]);

  function escolherTempo(minutos: number) {
    const p = new URLSearchParams(params);
    if (minutos === TEMPO_PADRAO) p.delete("tempo");
    else p.set("tempo", String(minutos));
    setParams(p, { replace: true });
  }

  function atualizarAula(aulaId: number, mudanca: Partial<AulaBonus>) {
    setEstado((e) =>
      e.fase !== "pronto"
        ? e
        : { ...e, curso: { ...e.curso, aulas: e.curso.aulas.map((a) => (a.aulaId === aulaId ? { ...a, ...mudanca } : a)) } },
    );
  }

  async function concluir(aula: AulaBonus, valor: boolean) {
    if (!curso) return;
    setOcupadas((s) => new Set(s).add(aula.aulaId));
    setErros((m) => {
      const n = new Map(m);
      n.delete(aula.aulaId);
      return n;
    });
    const r = await concluirAula(card, curso, aula.aulaId, valor);
    setOcupadas((s) => {
      const n = new Set(s);
      n.delete(aula.aulaId);
      return n;
    });
    if (r.ok) atualizarAula(aula.aulaId, { concluidaEm: r.concluidaEm });
    else setErros((m) => new Map(m).set(aula.aulaId, mensagemDaEscrita(r.motivo)));
  }

  async function anotar(aula: AulaBonus, texto: string): Promise<string | null> {
    if (!curso) return "sem curso";
    const r = await anotarAula(card, curso, aula.aulaId, texto);
    if (r.ok) {
      atualizarAula(aula.aulaId, { anotacao: r.anotacao });
      return null;
    }
    return mensagemDaEscrita(r.motivo);
  }

  if (hub.fase === "carregando" || estado.fase === "carregando") {
    return <Carregando texto="Carregando o seu plano…" />;
  }

  if (estado.fase === "recusado" || estado.fase === "erro") {
    const { titulo, texto } =
      estado.fase === "erro"
        ? { titulo: "Não conseguimos carregar o seu plano", texto: "O problema é nosso, não seu. Tente de novo em alguns minutos." }
        : mensagemDaAbertura(estado.motivo as MotivoAbertura);
    return (
      <Aviso
        titulo={titulo}
        acao={
          <Link to={curso ? `/curso/${curso}` : "/"} className="pill-cta">
            {curso ? "Voltar ao curso" : "Voltar ao início"}
          </Link>
        }
      >
        {texto}
      </Aviso>
    );
  }

  const { aulas } = estado.curso;
  const cards = hub.fase === "pronto" ? hub.hub.cardsDeCurso : [];
  const nomeDoCard = cards.find((c) => c.card_slug === card)?.card_nome ?? "Plano de Estudos Inteligente";
  const progresso = progressoDe(aulas);
  const hoje = aulasParaHoje(aulas, tempo);
  const estudado = segundosEstudados(aulas);
  const ponte = (a: AulaBonus) =>
    curso ? `/abrir/${encodeURIComponent(curso)}/${encodeURIComponent(card)}/${a.aulaId}` : null;
  const restante =
    progresso.concluidas > 0 && !progresso.concluido && (progresso.restanteSegundos ?? 0) > 0
      ? duracaoEmTexto(progresso.restanteSegundos)
      : null;

  const descricao = progresso.concluido ? (
    <>Você concluiu o plano inteiro. As aulas continuam abertas para rever quando quiser.</>
  ) : (
    <>
      Diga quanto tempo você tem hoje, e o plano mostra as próximas aulas do CFC de A a Z que cabem nele.
      As aulas abrem na plataforma de vídeo com você já conectado; marque cada uma como concluída e o
      plano segue de onde parou.
    </>
  );

  return (
    <main className="mx-auto w-full max-w-6xl px-4 pt-6 pb-12 sm:px-6">
      <Cabine
        rotulo="Exame CFC"
        titulo={nomeDoCard}
        descricao={descricao}
        fatos={
          progresso.total > 0 ? (
            <>
              <TileDeFato
                rotulo="Concluídas"
                valor={`${progresso.concluidas} de ${progresso.total}`}
                apoio={
                  progresso.concluido
                    ? "plano concluído"
                    : progresso.concluidas === 0
                      ? "nenhuma ainda"
                      : `${progresso.porcentagem}% do plano`
                }
              />
              {estudado > 0 && <TileDeFato rotulo="Estudado" valor={duracaoEmTexto(estudado) ?? ""} apoio="somando as aulas concluídas" />}
              {restante && <TileDeFato rotulo="Faltam" valor={restante} apoio="de vídeo por assistir" />}
            </>
          ) : undefined
        }
        rodape={
          progresso.total > 0 ? (
            // Uma barra só: com 339 aulas, um segmento por aula (a faixa dos cursos bônus) teria
            // segmentos mais finos que um pixel no celular.
            <div
              role="progressbar"
              aria-label="Aulas concluídas do plano"
              aria-valuemin={0}
              aria-valuemax={progresso.total}
              aria-valuenow={progresso.concluidas}
              data-barra-do-plano
              className="h-1.5 w-full overflow-hidden rounded-full bg-white/15"
            >
              <span className="block h-full rounded-full bg-laranja" style={{ width: `${progresso.porcentagem}%` }} />
            </div>
          ) : undefined
        }
      />

      {aulas.length === 0 ? (
        <p className="surface mt-6 p-6 text-sm text-cinza">
          O plano ainda não foi carregado aqui. O seu acesso está liberado — se demorar, o suporte resolve.
        </p>
      ) : (
        <>
          <section aria-labelledby="hoje-titulo" className="surface mt-6 p-5 sm:p-6" data-hoje>
            <h2 id="hoje-titulo" className="font-display text-lg font-bold text-titulo">
              Quanto tempo você tem hoje?
            </h2>
            <div role="radiogroup" aria-labelledby="hoje-titulo" className="mt-3 flex flex-wrap gap-2">
              {TEMPOS_DO_DIA.map((m) => {
                const escolhido = m === tempo;
                return (
                  <button
                    key={m}
                    type="button"
                    role="radio"
                    aria-checked={escolhido}
                    data-tempo={m}
                    onClick={() => escolherTempo(m)}
                    className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition ${
                      escolhido
                        ? "border-acento bg-acento text-acento-tinta"
                        : "border-borda bg-superficie text-tinta hover:border-acento"
                    }`}
                  >
                    {rotuloDoTempo(m)}
                  </button>
                );
              })}
            </div>

            {progresso.concluido ? (
              <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-aviso-tinta" data-plano-concluido>
                <Tique className="size-4" />
                Você concluiu todas as aulas do plano.
              </p>
            ) : (
              <>
                <p className="mt-4 text-sm text-cinza" data-resumo-de-hoje>
                  {hoje.aulas.length === 1 ? "1 aula" : `${hoje.aulas.length} aulas`} para hoje ·{" "}
                  {duracaoEmTexto(hoje.segundos) ?? "sem duração"} de vídeo
                </p>
                {hoje.primeiraPassaDoTempo && (
                  <p className="mt-1 text-sm text-alerta-tinta" data-passa-do-tempo>
                    A próxima aula tem {duracaoEmTexto(hoje.aulas[0].duracaoSegundos)}, mais que o tempo
                    escolhido. Ela fica na lista porque o plano segue por ela.
                  </p>
                )}
                <ol className="mt-3 divide-y divide-borda overflow-hidden rounded-xl border border-borda" data-lista-de-hoje>
                  {hoje.aulas.map((a, i) => {
                    const numero = numeroDa(aulas, a.aulaId) ?? i + 1;
                    return (
                      <LinhaDaAula
                        key={a.aulaId}
                        aula={a}
                        numero={numero}
                        para={ponte(a)}
                        ehProxima={i === 0}
                        ocupado={ocupadas.has(a.aulaId)}
                        erro={erros.get(a.aulaId) ?? null}
                        anotacaoAberta={anotacaoAberta === a.aulaId}
                        onConcluir={(valor) => concluir(a, valor)}
                        onAlternarAnotacao={() => setAnotacaoAberta((atual) => (atual === a.aulaId ? null : a.aulaId))}
                      >
                        <Anotacao
                          aulaId={a.aulaId}
                          numero={numero}
                          inicial={a.anotacao}
                          aberta={anotacaoAberta === a.aulaId}
                          onSalvar={(texto) => anotar(a, texto)}
                        />
                      </LinhaDaAula>
                    );
                  })}
                </ol>
              </>
            )}
          </section>

          <PlanoInteiro
            aulas={aulas}
            ponte={ponte}
            ocupadas={ocupadas}
            erros={erros}
            onConcluir={concluir}
          />

          <p className="mt-6 text-xs leading-relaxed text-cinza">
            Marcar uma aula como concluída registra o seu progresso aqui no hub. A plataforma de vídeo tem a
            própria marcação, e as duas não se conversam.
          </p>
        </>
      )}
    </main>
  );
}

/**
 * O plano inteiro, fechado por padrão: são 339 aulas, e aberto ele ocuparia dezenas de telas acima de
 * nada. Agrupa as aulas SEGUIDAS da mesma matéria (`porSecao`), sem reordenar: a ordem de estudo
 * atravessa as matérias, e a mesma matéria pode voltar mais adiante.
 *
 * A linha é compacta e não tem a anotação: a anotação mora na lista de hoje, e a mesma aula nas duas
 * listas teria dois painéis com o mesmo id.
 */
function PlanoInteiro({
  aulas,
  ponte,
  ocupadas,
  erros,
  onConcluir,
}: {
  aulas: AulaBonus[];
  ponte: (a: AulaBonus) => string | null;
  ocupadas: Set<number>;
  erros: Map<number, string>;
  onConcluir: (a: AulaBonus, valor: boolean) => void;
}) {
  const grupos = porSecao(aulas);
  let n = 0;
  return (
    <details className="surface mt-6 overflow-hidden" data-plano-inteiro>
      <summary className="cursor-pointer px-5 py-4 font-display font-bold text-titulo sm:px-6">
        O plano inteiro · {aulas.length} aulas
      </summary>
      <div className="border-t border-borda">
        {grupos.map((g, gi) => (
          <section key={`${g.secao ?? "sem"}-${gi}`} aria-label={g.secao ?? "Aulas"} className="border-b border-borda last:border-b-0">
            <h3 className="bg-tile px-5 py-2 text-xs font-bold tracking-wide text-alerta-tinta uppercase sm:px-6">
              {g.secao ?? "Aulas"}
            </h3>
            <ol>
              {g.aulas.map((a) => {
                n += 1;
                const numero = n;
                const concluida = a.concluidaEm !== null;
                const para = ponte(a);
                const erro = erros.get(a.aulaId);
                return (
                  <li key={a.aulaId} data-aula-do-plano={a.aulaId} data-concluida={concluida ? "sim" : "nao"}
                      className="flex flex-wrap items-center gap-3 px-5 py-2.5 sm:px-6">
                    <span aria-hidden="true" className="w-8 shrink-0 text-right font-display text-sm font-bold text-cinza">
                      {numero}
                    </span>
                    <span className={`min-w-0 flex-1 basis-48 text-sm ${concluida ? "text-cinza" : "text-tinta"}`}>
                      <span className="sr-only">Aula {numero}: </span>
                      {a.titulo}
                      {duracaoEmTexto(a.duracaoSegundos) && (
                        <span className="text-cinza"> · {duracaoEmTexto(a.duracaoSegundos)}</span>
                      )}
                      {erro && <span role="alert" className="block text-xs text-erro-tinta">{erro}</span>}
                    </span>
                    {para && (
                      <a href={para} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-sm font-semibold text-alerta-tinta hover:underline">
                        Assistir
                        <IconeExterno className="size-3.5" />
                      </a>
                    )}
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={concluida}
                      aria-label={concluida ? `Desmarcar a aula ${numero} como concluída` : `Marcar a aula ${numero} como concluída`}
                      disabled={ocupadas.has(a.aulaId)}
                      onClick={() => onConcluir(a, !concluida)}
                      data-concluir-no-plano={a.aulaId}
                      className={`grid size-8 shrink-0 place-items-center rounded-full border-2 transition disabled:opacity-60 ${
                        concluida
                          ? "border-aviso-borda bg-aviso-fundo text-aviso-tinta"
                          : "border-borda bg-superficie text-cinza hover:border-laranja hover:text-laranja"
                      }`}
                    >
                      <Tique className="size-3.5" />
                    </button>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}
      </div>
    </details>
  );
}
